/**
 * Document identity (PD-9). The prototype hashed only the first 10 KB, so
 * two EPUBs from the same publisher (identical frontmatter) collided and
 * hijacked each other's resume position. v1 identity: SHA-256 over
 * (size ‖ first 64 KiB ‖ last 64 KiB), hex; size is stored alongside.
 * Filename stays a display-level tiebreaker only (never part of identity).
 */

export const IDENTITY_SAMPLE_BYTES = 64 * 1024;

export interface ContentIdentity {
  readonly contentHash: string;
  readonly size: number;
}

function toHex(digest: ArrayBuffer): string {
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * size ‖ first-64KiB ‖ last-64KiB. Size is encoded as ASCII decimal + space so
 * the sample chunks can never be confused across different-length inputs.
 * For inputs under 128 KiB the chunks overlap — deterministic and fine.
 */
export async function contentIdentity(bytes: Uint8Array): Promise<ContentIdentity> {
  const size = bytes.byteLength;
  const head = bytes.subarray(0, Math.min(IDENTITY_SAMPLE_BYTES, size));
  const tail = bytes.subarray(Math.max(size - IDENTITY_SAMPLE_BYTES, 0));
  const sizeAscii = new TextEncoder().encode(`${size} `);
  const payload = new Uint8Array(sizeAscii.length + head.length + tail.length);
  payload.set(sizeAscii, 0);
  payload.set(head, sizeAscii.length);
  payload.set(tail, sizeAscii.length + head.length);
  const digest = await crypto.subtle.digest('SHA-256', payload);
  return { contentHash: toHex(digest), size };
}

/** Prototype file identity (§6.6 matching only): SHA-256 of the first 10 KiB. */
export async function legacyFileHash(bytes: Uint8Array): Promise<string> {
  // Copy: subarray() may be SharedArrayBuffer-backed, which BufferSource rejects.
  const digest = await crypto.subtle.digest('SHA-256', new Uint8Array(bytes.subarray(0, 10240)));
  return toHex(digest);
}

/** Prototype paste identity (§6.6 matching only): djb2-style over the first
 * 10 000 UTF-16 units, "txt_" + abs hex — byte-for-byte the old algorithm. */
export function legacyTextHash(text: string): string {
  let hash = 0;
  for (let i = 0; i < Math.min(text.length, 10000); i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i);
    hash |= 0;
  }
  return 'txt_' + Math.abs(hash).toString(16);
}
